/**
 * 协调记录面板组件
 */
import React, { useState, useEffect } from 'react';
import { Table, Spin, Empty, message, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CoordinationCostService } from '../../../services/coordinationCostService';
import { formatMoney } from '../../../utils/budgetUtils';
import { CoordinationCostType } from '../../../types';

interface CoordinationPanelProps {
  projectId: string;
  isMobile?: boolean;
  active?: boolean;
}

interface CoordinationRecord {
  _id: string;
  costDate: string;
  costType: CoordinationCostType;
  amount: number;
  description?: string;
  handler?: string;
}

const getTypeTag = (type: CoordinationCostType) => {
  const colorMap: Record<CoordinationCostType, string> = {
    [CoordinationCostType.MANAGEMENT]: 'blue',
    [CoordinationCostType.COORDINATION]: 'cyan',
    [CoordinationCostType.MISC]: 'default',
    [CoordinationCostType.EMERGENCY]: 'red',
  };

  return (
    <Tag color={colorMap[type]}>
      {CoordinationCostService.getCostTypeLabel(type)}
    </Tag>
  );
};

const CoordinationPanel: React.FC<CoordinationPanelProps> = ({
  projectId,
  isMobile = false,
  active = false
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<CoordinationRecord[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: isMobile ? 5 : 10,
    total: 0
  });
  const [loaded, setLoaded] = useState(false);

  const fetchData = async (page = 1, pageSize = isMobile ? 5 : 10) => {
    if (!projectId) return;

    setLoading(true);
    try {
      const response = await CoordinationCostService.getCoordinationCosts({
        projectId,
        page,
        limit: pageSize
      });

      if (response.success && response.data) {
        setData(response.data);
        setPagination({
          current: response.pagination?.page || page,
          pageSize: response.pagination?.limit || pageSize,
          total: response.pagination?.total || 0
        });
      }
    } catch (error) {
      console.error('获取协调记录失败:', error);
      message.error('获取协调记录失败');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (active && !loaded) {
      fetchData();
    }
  }, [active, loaded, projectId]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return dateStr.split('T')[0];
  };

  const columns: ColumnsType<CoordinationRecord> = isMobile ? [
    {
      title: '类型/日期',
      key: 'info',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{CoordinationCostService.getCostTypeLabel(record.costType)}</div>
          <div style={{ fontSize: 12, color: '#666' }}>
            {formatDate(record.costDate)}{record.handler ? ` · ${record.handler}` : ''}
          </div>
          {record.description && (
            <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
              {record.description}
            </div>
          )}
        </div>
      )
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      align: 'right',
      render: (val: number) => formatMoney(val || 0, false)
    }
  ] : [
    {
      title: '日期',
      dataIndex: 'costDate',
      key: 'costDate',
      width: 120,
      render: (val: string) => formatDate(val)
    },
    {
      title: '类型',
      dataIndex: 'costType',
      key: 'costType',
      width: 180,
      render: (val: CoordinationCostType) => getTypeTag(val)
    },
    {
      title: '经办人',
      dataIndex: 'handler',
      key: 'handler',
      width: 120,
      render: (val: string) => val || '-'
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      align: 'right',
      render: (val: number) => formatMoney(val || 0, false)
    },
    {
      title: '备注',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (val: string) => val || '-'
    }
  ];

  if (!loaded && !active) {
    return null;
  }

  if (loading && !data.length) {
    return (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <Spin tip="加载中..." />
      </div>
    );
  }

  if (!data.length) {
    return <Empty description="暂无协调记录" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <Table
      columns={columns}
      dataSource={data}
      rowKey="_id"
      loading={loading}
      size="small"
      pagination={{
        ...pagination,
        size: 'small',
        showSizeChanger: false,
        showTotal: (total) => `共 ${total} 条`,
        onChange: (page, pageSize) => fetchData(page, pageSize)
      }}
    />
  );
};

export default CoordinationPanel;
